<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '$lib/components/common/button/Button.svelte';
  import { showToast } from '$lib/helpers/toastrHelper';
  import { fetchWithAuth } from '$lib/utils/http/httpUtil';
  import { Info, Warning, Eye, EyeSlash } from 'phosphor-svelte';
  import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
  
  export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  
  // Entity selection
  type Entity = {
    id: number;
    name: string;
    code: string;
    hasMYOBAdvancedConfig: boolean;
  };
  
  let entities: Entity[] = [];
  let selectedEntity: Entity | null = null;
  let newEntityName: string = '';
  let isLoadingEntities = false;
  let showConfigSection = false;
  
  // MYOB Advanced setup parameters
  let baseURL = 'https://eplace.myobadvanced.com';
  let userName = '';
  let userPass = '';
  let endpointName = 'Default';
  let endpointVersion = '23.200.001';
  let companyCode = '';
  
  // Form state management
  let isLoading = false;
  let isExecuting = false;
  let hasExistingConfig = false;
  let existingConfigIds: any[] = [];
  
  // Single function to update form data from server response
  function updateFormFromConfig(config: any) {
    baseURL = config.baseURL || 'https://eplace.myobadvanced.com';
    userName = config.userName || '';
    userPass = config.userPass || '';
    endpointName = config.endpointName || 'Default';
    endpointVersion = config.endpointVersion || '23.200.001';
    companyCode = config.companyCode || '';
    existingConfigIds = config.configurationIds || [];
  }
  
  // Password visibility toggle
  let showUserPass = false;
  
  // URL validation
  let urlError = '';
  
  // Common fetch error handler
  function handleApiError(error: any, message: string) {
    console.error(`${message}:`, error);
    showToast({
      title: 'Error',
      message: error?.message || message,
      type: 'error',
    });
  }
  
  function validateBaseURL(url: string): boolean {
    // Reset error if empty
    if (!url) {
      urlError = '';
      return false;
    }
    
    // Check for HTTPS protocol
    const isHttps = url.startsWith('https://');
    if (!isHttps) {
      urlError = 'URL must start with https://';
      return false;
    }
    
    // URL is valid
    urlError = '';
    return true;
  }
  
  $: isUrlValid = validateBaseURL(baseURL);
  $: formIsValid = !!(clientId && baseURL && isUrlValid && userName && userPass && endpointName && endpointVersion && companyCode);
  $: canSave = formIsValid && !isExecuting;
  $: showConfigSection = !!(selectedEntity || newEntityName);

  onMount(async () => {
    if (clientId) {
      await loadInitialEntities();
    }
  });
  
  async function loadInitialEntities() {
    if (!clientId || isLoadingEntities) return;
    
    isLoadingEntities = true;
    
    try {
      const response = await fetchWithAuth(`/api/integration/myobadvanced-entities/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          
          if (entityId) {
            const existingEntity = entities.find(e => e.id.toString() === entityId);
            if (existingEntity) {
              newEntityName = ''; 
              selectedEntity = existingEntity;
              await loadExistingMYOBAdvancedConfig();
            } else {
              entityId = '';
            }
          }
        } else {
          showToast({
            title: 'Error',
            message: result.error || 'Failed to load entities',
            type: 'error',
          });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadExistingMYOBAdvancedConfig() {
    if (!clientId || !entityId || isLoading) return;

    isLoading = true;
    hasExistingConfig = false;

    try {
      const response = await fetchWithAuth(`/api/integration/myobadvanced-config/${clientId}/${entityId || 0}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.exists) {
          hasExistingConfig = true;
          
          updateFormFromConfig(result.data.config);

          showToast({
            title: 'Existing Configuration Loaded',
            message: 'This client has existing MYOB Advanced configuration.',
            type: 'info',
          });
        } else {
          hasExistingConfig = false;
        }
      }
    } catch (error) {
      console.error('Failed to check existing MYOB Advanced config:', error);
      hasExistingConfig = false;
    } finally {
      isLoading = false;
    }
  }

  function createRequestBody() {
    return {
      clientId: parseInt(clientId),
      entityId: entityId ? parseInt(entityId) : 0,
      baseURL,
      userName,
      userPass,
      endpointName,
      endpointVersion,
      companyCode,
      entityName: newEntityName || selectedEntity?.name || clientName,
      existingConfigIds: hasExistingConfig ? existingConfigIds : null
    };
  }
  
  async function saveMYOBAdvancedConfiguration() {
    if (!canSave) return;
    
    isExecuting = true;

    try {
      const requestBody = createRequestBody();

      const response = await fetchWithAuth('/api/integration/myobadvanced-setup', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        const newEntityId = result.data.entityId;

        showToast({
          title: 'Success',
          message: 'MYOB Advanced configuration saved successfully',
          type: 'success',
        });
        
        if (!entityId && newEntityId) {
          entityId = newEntityId.toString();
          hasExistingConfig = true;        
          
          await loadEntitiesWithoutResetting();
          
          const createdEntity = entities.find(e => e.id === newEntityId);
          if (createdEntity) {
            newEntityName = '';
            selectedEntity = createdEntity;
          }
        } else {
          await loadEntitiesWithoutResetting();
        }
      } else {
        let errorMessage = 'Failed to save MYOB Advanced configuration';
        if (Array.isArray(result.errors)) {
          errorMessage = result.errors.join(', ');
        } else if (typeof result.errors === 'string') {
          errorMessage = result.errors;
        } else if (result.errors) {
          errorMessage = JSON.stringify(result.errors);
        }
        
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save MYOB Advanced configuration';
      
      handleApiError(error, 'Failed to save MYOB Advanced configuration');
    } finally {
      isExecuting = false;
    }
  }

  function resetForm() {
    baseURL = 'https://eplace.myobadvanced.com';
    userName = '';
    userPass = '';
    endpointName = 'Default';
    endpointVersion = '23.200.001';
    companyCode = '';
    hasExistingConfig = false;
    existingConfigIds = [];
  }
  
  function resetAll() {
    resetForm();
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
    showConfigSection = false;
  }

  async function loadEntitiesWithoutResetting() {
    if (!clientId || isLoadingEntities) return;
    
    isLoadingEntities = true;
    
    try {
      const response = await fetchWithAuth(`/api/integration/myobadvanced-entities/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          // Store current entity selection to restore it after refresh
          const currentEntityId = selectedEntity?.id;
          
          // Update entities list
          entities = result.entities;
          
          // Restore entity selection if it still exists in the refreshed list
          if (currentEntityId) {
            const updatedEntity = entities.find(e => e.id === currentEntityId);
            if (updatedEntity) {
              selectedEntity = updatedEntity;
            }
          }
        } else {
          console.error('Failed to load entities:', result.error);
        }
      }
    } catch (error) {
      console.error('Failed to load entities:', error);
    } finally {
      isLoadingEntities = false;
    }
  }
  
  async function loadEntities() {
    if (!clientId || isLoadingEntities) return;
    
    isLoadingEntities = true;
    
    // Hide MYOB Advanced API Configuration Card when refreshing entities
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
    resetForm();
    
    try {
      const response = await fetchWithAuth(`/api/integration/myobadvanced-entities/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          
          showToast({
            title: 'Success',
            message: 'Entities refreshed successfully',
            type: 'success',
          });
        } else {
          showToast({
            title: 'Error',
            message: result.error || 'Failed to load entities',
            type: 'error',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load entities:', error);
      showToast({
        title: 'Error',
        message: 'Failed to load entities',
        type: 'error',
      });
    } finally {
      isLoadingEntities = false;
    }
  }
  
  function handleEntitySelection() {
    // This function is called when the dropdown selection changes
    if (selectedEntity) {
      newEntityName = '';
      entityId = selectedEntity.id.toString();
      
      // Load config when entity is selected
      if (selectedEntity.hasMYOBAdvancedConfig) {
        loadExistingMYOBAdvancedConfig();
      } else {
        resetForm();
        hasExistingConfig = false;
      }
    } else {
      entityId = '';
    }
  }
  
  function handleNewEntityInput() {
    // This function is called when typing in the new entity input
    if (newEntityName) {
      if (selectedEntity !== null) {
        selectedEntity = null;
        entityId = '';
        resetForm();
        hasExistingConfig = false;
      }
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">MYOB Advanced Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure MYOB Advanced integration for this client with API credentials and endpoint settings.</p>
  
  <!-- CARD 1: Entity Selection Card -->
  <div class="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h4 class="mb-4 font-semibold">Select or Create Entity</h4>
    <p class="mb-4 text-sm text-gray-600">Choose an existing entity or create a new one for MYOB Advanced integration.</p>
    
    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <!-- Left side: Existing Entity Selection -->
      <div>
        <label for="entity-select" class="mb-2 block text-sm font-medium text-gray-700">
          Existing Entity
        </label>
        <div class="flex items-end gap-3">
          <select
            id="entity-select"
            bind:value={selectedEntity}
            on:change={handleEntitySelection}
            class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
            disabled={isLoadingEntities || newEntityName !== '' || isExecuting}
          >
            <option value={null}>-- Select Entity --</option>
            {#each entities as entity}
              <option value={entity}>{entity.name} {entity.hasMYOBAdvancedConfig ? '(has MYOB Advanced config)' : ''}</option>
            {/each}
          </select>
          <Button
            label={isLoadingEntities ? "Loading..." : "Refresh"}
            onClick={loadEntities}
            disabled={isLoadingEntities || isExecuting}
            colour="secondary"
            size="small"
          />
        </div>
        {#if isLoadingEntities}
          <p class="mt-2 text-sm text-gray-500">Loading entities...</p>
        {/if}
      </div>
      
      <!-- Right side: New Entity Creation -->
      <div>
        <label for="new-entity-name" class="mb-2 block text-sm font-medium text-gray-700">
          New Entity Name
        </label>
        <input
          id="new-entity-name"
          type="text"
          bind:value={newEntityName}
          on:input={handleNewEntityInput}
          placeholder="Enter new entity name"
          class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
          disabled={selectedEntity !== null || isExecuting}
        />
        <p class="mt-2 text-xs text-gray-500">Leave blank to select an existing entity</p>
      </div>
    </div>
  </div>
  
  <!-- CARD 2: MYOB Advanced Configuration Card (only shown when entity is selected or new entity name provided) -->
  {#if showConfigSection}
    <div class="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div class="flex items-center justify-between">
        <h4 class="font-semibold">MYOB Advanced API Configuration</h4>
        {#if isLoading}
          <span class="text-sm text-gray-500">Loading existing configuration...</span>
        {:else if hasExistingConfig}
          <div class="flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1 text-sm text-blue-700">
            <Info size={16} />
            <span>Updating existing configuration</span>
          </div>
        {/if}
      </div>
      
      {#if selectedEntity}
        <div class="rounded-md bg-blue-50 p-3 text-sm text-blue-700">
          <p>Configuring for entity: <strong>{selectedEntity.name}</strong> {selectedEntity.code ? `(Code: ${selectedEntity.code})` : ''}</p>
        </div>
      {:else if newEntityName}
        <div class="rounded-md bg-green-50 p-3 text-sm text-green-700">
          <p>Creating new entity: <strong>{newEntityName}</strong></p>
        </div>
      {/if}

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label for="base-url" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Base URL
          <RequiredBadge />
        </label>
        <input
          id="base-url"
          type="url"
          bind:value={baseURL}
          placeholder="https://api.myobadvanced.com"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2"
          class:border-gray-300={baseURL && isUrlValid}
          class:border-primary-200={!baseURL}
          class:border-error-500={baseURL && !isUrlValid}
          class:ring-error-300={baseURL && !isUrlValid}
          class:ring-primary-500={baseURL && isUrlValid}
          disabled={isExecuting}
        />
        {#if baseURL && urlError}
          <p class="mt-1 text-xs text-error-500">{urlError}</p>
        {/if}
      </div>
      <div>
        <label for="company-code" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Company Code
          <RequiredBadge />
        </label>
        <input
          id="company-code"
          type="text"
          bind:value={companyCode}
          placeholder="COMP01"
          maxlength="20"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={companyCode}
          class:border-primary-200={!companyCode}
          disabled={isExecuting}
        />
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label for="user-name" class="mb-2 flex items-center gap-2 text-sm font-medium">
          User Name
          <RequiredBadge />
        </label>
        <input
          id="user-name"
          type="text"
          bind:value={userName}
          placeholder="api_user"
          maxlength="100"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={userName}
          class:border-primary-200={!userName}
          disabled={isExecuting}
        />
      </div>
      <div>
        <label for="user-pass" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Password
          <RequiredBadge />
        </label>
        <div class="relative">
          {#if showUserPass}
            <input
              id="user-pass"
              type="text"
              bind:value={userPass}
              placeholder="Enter password"
              maxlength="100"
              class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
              class:border-gray-300={userPass}
              class:border-primary-200={!userPass}
              disabled={isExecuting}
            />
          {:else}
            <input
              id="user-pass"
              type="password"
              bind:value={userPass}
              placeholder="Enter password"
              maxlength="100"
              class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
              class:border-gray-300={userPass}
              class:border-primary-200={!userPass}
              disabled={isExecuting}
            />
          {/if}
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            on:click={() => (showUserPass = !showUserPass)}
            disabled={isExecuting}
          >
            {#if showUserPass}
              <EyeSlash size={20} />
            {:else}
              <Eye size={20} />
            {/if}
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label for="endpoint-name" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Endpoint Name
          <RequiredBadge />
        </label>
        <input
          id="endpoint-name"
          type="text"
          bind:value={endpointName}
          placeholder="DefaultEndpoint"
          maxlength="100"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={endpointName}
          class:border-primary-200={!endpointName}
          disabled={isExecuting}
        />
      </div>
      <div>
        <label for="endpoint-version" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Endpoint Version
          <RequiredBadge />
        </label>
        <input
          id="endpoint-version"
          type="text"
          bind:value={endpointVersion}
          placeholder="23.200.001"
          maxlength="20"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={endpointVersion}
          class:border-primary-200={!endpointVersion}
          disabled={isExecuting}
        />
      </div>
    </div>
  </div>
  {/if}
    
  <div class="mt-6 flex justify-end gap-3">
    <Button label="Reset All" onClick={resetAll} disabled={isExecuting} colour="secondary" />
    {#if showConfigSection}
      <Button 
        label={isExecuting ? 'Saving...' : hasExistingConfig ? 'Update Configuration' : 'Save Configuration'} 
        onClick={saveMYOBAdvancedConfiguration} 
        disabled={!canSave} 
        colour="primary" 
      />
    {/if}
  </div>
</div>
